// src/app/lib/document-scan.ts
/** Foto-Zuschnitt/Perspektivkorrektur — reines Canvas-2D/JS, kein OpenCV/WASM (siehe
 *  Ticket 2026-07-22: das ~15,5 MB OpenCV.js-WASM-Modul blieb in Safari beim
 *  Initialisieren hängen, auch mit großzügigem Timeout — kompletter Ersatz statt
 *  weiterer Zeit-Tuning-Versuche). Bewusst NICHT automatisiert getestet (jsdom hat kein
 *  echtes Canvas) — die reine Mathe dahinter (computeHomography/applyHomography) liegt
 *  getestet in document-scan-geometry.ts. `PhotoCorrection.tsx` mockt dieses Modul
 *  komplett. */
import { applyHomography, computeHomography, type Point } from './document-scan-geometry';

/** Lädt eine Bild-Datei als ImageBitmap (native Browser-Dekodierung, kein Netzwerk/WASM
 *  nötig) und liefert die Original-Pixelmaße. */
export async function fileToBitmap(file: File): Promise<{ bitmap: ImageBitmap; width: number; height: number }> {
  const bitmap = await createImageBitmap(file);
  return { bitmap, width: bitmap.width, height: bitmap.height };
}

/** Entzerrt `bitmap` auf ein gerades Rechteck der Größe `outputSize`, anhand der 4
 *  (bereits geordneten) Eckpunkte in Original-Bildkoordinaten. Rückwärts-Abbildung
 *  (Ziel-Pixel → Quell-Pixel via Homographie) + bilineare Interpolation — vermeidet
 *  Löcher im Ergebnis, die bei einer Vorwärts-Abbildung entstehen würden. */
export function warpToRectangle(
  bitmap: ImageBitmap,
  orderedCorners: [Point, Point, Point, Point],
  outputSize: { width: number; height: number },
): ImageData {
  const { width, height } = outputSize;

  const srcCanvas = document.createElement('canvas');
  srcCanvas.width = bitmap.width;
  srcCanvas.height = bitmap.height;
  const srcCtx = srcCanvas.getContext('2d')!;
  srcCtx.drawImage(bitmap, 0, 0);
  const src = srcCtx.getImageData(0, 0, bitmap.width, bitmap.height);

  const destRect: [Point, Point, Point, Point] = [
    { x: 0, y: 0 },
    { x: width, y: 0 },
    { x: width, y: height },
    { x: 0, y: height },
  ];
  // dest → src, direkt in der Richtung, die die Pixel-Schleife unten braucht.
  const homography = computeHomography(destRect, orderedCorners);

  const dest = new ImageData(width, height);
  for (let dy = 0; dy < height; dy++) {
    for (let dx = 0; dx < width; dx++) {
      const { x: sx, y: sy } = applyHomography(homography, { x: dx, y: dy });
      sampleBilinear(src, sx, sy, dest, dx, dy);
    }
  }
  return dest;
}

function sampleBilinear(src: ImageData, sx: number, sy: number, dest: ImageData, dx: number, dy: number): void {
  const di = (dy * dest.width + dx) * 4;
  if (sx < 0 || sy < 0 || sx > src.width - 1 || sy > src.height - 1) {
    dest.data[di + 3] = 0; // außerhalb der Quelle → transparent
    return;
  }
  const x0 = Math.floor(sx);
  const y0 = Math.floor(sy);
  const x1 = Math.min(x0 + 1, src.width - 1);
  const y1 = Math.min(y0 + 1, src.height - 1);
  const fx = sx - x0;
  const fy = sy - y0;
  for (let c = 0; c < 4; c++) {
    const p00 = src.data[(y0 * src.width + x0) * 4 + c];
    const p10 = src.data[(y0 * src.width + x1) * 4 + c];
    const p01 = src.data[(y1 * src.width + x0) * 4 + c];
    const p11 = src.data[(y1 * src.width + x1) * 4 + c];
    const top = p00 + (p10 - p00) * fx;
    const bottom = p01 + (p11 - p01) * fx;
    dest.data[di + c] = Math.round(top + (bottom - top) * fy);
  }
}

/** Wandelt ImageData in ein JPEG-Blob (für Vorschau + Upload). */
export function imageDataToBlob(data: ImageData, quality = 0.92): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = data.width;
  canvas.height = data.height;
  canvas.getContext('2d')!.putImageData(data, 0, 0);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('toBlob lieferte kein Ergebnis'))),
      'image/jpeg',
      quality,
    );
  });
}

/** Dreht ein Bild um 90° (Kunde tippt den Dreh-Button, wenn das entzerrte Ergebnis
 *  seitlich rauskommt — die Perspektivkorrektur allein weiß nicht, welche Ecke "oben"
 *  ist). Reine Canvas-2D-Operation. */
export function rotateCanvas90(
  source: CanvasImageSource & { width: number; height: number },
  direction: 1 | -1,
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = source.height;
  canvas.height = source.width;
  const ctx = canvas.getContext('2d')!;
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((direction * 90 * Math.PI) / 180);
  ctx.drawImage(source, -source.width / 2, -source.height / 2);
  return canvas;
}
